// contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { message } from "antd";
import { setOnUnauthorized } from "../axiosSetup";

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Read stored auth synchronously so the very first render already knows the
// user is logged in — initializing in a useEffect paints the login page for
// one frame on every refresh (the "login flash").
function readStoredAuth() {
    try {
        const storedAuth = localStorage.getItem('auth');
        if (storedAuth) {
            const parsedAuth = JSON.parse(storedAuth);
            if (parsedAuth?.token) {
                // Keep the flat token key in sync — every API call reads it
                localStorage.setItem('accessToken', parsedAuth.token);
                return {
                    id: parsedAuth.id,
                    token: parsedAuth.token,
                    user: parsedAuth.user,
                    isAuthenticated: true
                };
            }
        }
    } catch (error) {
        console.error('Error initializing auth:', error);
        localStorage.removeItem('auth');
    }
    return { id: null, token: null, user: null, isAuthenticated: false };
}

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState(readStoredAuth);

    // Auto-logout when the backend rejects the token ("not logged in") —
    // registered once; the axios response interceptor calls this.
    useEffect(() => {
        setOnUnauthorized(() => {
            // Skip if already logged out (e.g. race between parallel calls)
            if (!localStorage.getItem('auth')) return;
            setAuthState({ id: null, token: null, user: null, isAuthenticated: false });
            localStorage.removeItem('auth');
            localStorage.removeItem('accessToken');
            message.warning("Your session has expired — please sign in again.", 6);
        });
        return () => setOnUnauthorized(null);
    }, []);

    // Login function
    const login = (userData) => {
        const authData = {
            id: userData.id,
            token: userData.token,
            user: userData.user,
            isAuthenticated: true
        };

        setAuthState(authData);
        localStorage.setItem('auth', JSON.stringify(authData));
        if (userData.token) localStorage.setItem('accessToken', userData.token);
    };

    // Logout function
    const logout = () => {
        setAuthState({
            id: null,
            token: null,
            user: null,
            isAuthenticated: false
        });
        localStorage.removeItem('auth');
        localStorage.removeItem('accessToken');
    };

    // Update user data
    const updateUser = (userData) => {
        const updatedAuth = {
            ...authState,
            user: { ...authState.user, ...userData }
        };
        setAuthState(updatedAuth);
        localStorage.setItem('auth', JSON.stringify(updatedAuth));
    };

    const value = {
        authState,
        login,
        logout,
        updateUser,
        isAuthenticated: authState.isAuthenticated
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Keep the default export for backward compatibility
export { AuthContext };